























































































import { Component, Vue, Watch } from "vue-property-decorator";
import { setUpdateHold } from "@/utils/UpdateHold";
import { GenericDevice } from "@/data/models/GenericDevice";
import ClientPanel from "@/data/models/ClientPanel";
import { getAvailablePanels } from "@/data/repository/ClientPanelRepository";
import {
  createLine,
  getLocalizedCSVHeaders,
} from "@/utils/devices.csv";
import {
  clientPanelToGenericDevice,
  getCategoryName,
  getDeviceName,
  getClientDevices
} from "@/data/repository/GenericDeviceRepository";
import { getDateForFile } from "@/utils/DateFormatter";
import { filterRetainedDevices, isDeviceAvailable } from "@/utils/DeviceManagementUtils";
import { GenericDeviceFilter, getGenericFilterName } from "@/data/models/DeviceFilter";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import { CSV_SEPARATOR } from "@/utils/Utils";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";
import { getUpdatedCategoryAndTemplateIds } from "@/data/models/DeviceSettingsManager";
import { COLOR_BUTTON_GREEN } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { ViewerSection } from "@/components/common/data-viewer/DataViewerModels";
import DataViewerComponent from "@/components/common/data-viewer/DataViewer.vue";
import { mapDeviceCsvToSection } from "@/utils/DeviceCsvToViewer";
import {
  downloadDeviceExport,
  ExportFormat,
  getCsvExportFormatLabel,
  getExportFormatIcon,
} from "@/utils/DeviceExportFormats";


enum DeviceTypes {
  GENERIC_DEVICES,
  ALL_DEVICES,
  IRES_DEVICES
} 

@Component({
  components: {
    "picker": PickerDialogComponent,
    "data-viewer": DataViewerComponent,
  }
})

export default class DeviceExport extends Vue {

  allDevices: (GenericDevice | ClientPanel)[];
  clientPanels: ClientPanel[] = [];
  devices: GenericDevice[];
  isLoading = true;
  isLoadingExport = false;
  showPreview = false;
  previewSections: ViewerSection[] = [];
  private exportCsvContent = "";
  private exportHeaders: string[] = [];
  private exportDataRows: string[] = [];
  selectedFormat = ExportFormat.CSV;

  /**
   * Holds the "new version" banner back while an export is running: a reload would abandon it
   * halfway through, with nothing downloaded. See `@/utils/UpdateHold`.
   */
  @Watch("isLoadingExport")
  onIsLoadingExportChanged(isLoadingExport: boolean) {
    setUpdateHold("device-export", isLoadingExport);
  }

  beforeDestroy() {
    setUpdateHold("device-export", false);
  }

  /** The three export formats, each with the icon that names that file type app-wide. */
  get formatOptions() {
    return [
      {
        label: getCsvExportFormatLabel(),
        value: ExportFormat.CSV,
        icon: getExportFormatIcon(ExportFormat.CSV),
      },
      {
        label: "Excel (.xlsx)",
        value: ExportFormat.EXCEL,
        icon: getExportFormatIcon(ExportFormat.EXCEL),
      },
      { label: "JSON", value: ExportFormat.JSON, icon: getExportFormatIcon(ExportFormat.JSON) },
    ];
  }

  devicesSelection = DeviceTypes.GENERIC_DEVICES;
  devicesSelectionOptions = [
    DeviceTypes.GENERIC_DEVICES,
    DeviceTypes.IRES_DEVICES,
    DeviceTypes.ALL_DEVICES
  ];

  deviceFilter = GenericDeviceFilter.ALL_DEVICES;
  deviceFilterOptions = [
    GenericDeviceFilter.ALL_DEVICES,
    GenericDeviceFilter.IN_USAGE,
    GenericDeviceFilter.AVAILABLE,
    GenericDeviceFilter.NOT_AVAILABLE
  ];

  get buttonColor() {
    return themedFill(COLOR_BUTTON_GREEN);
  }

  get buttonTextClass() {
    return themedFillTextClass(COLOR_BUTTON_GREEN, "white--text");
  }

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  get formatDevicesSelectionOptions() {
    return (type: DeviceTypes) => {
      switch (type) {
        case DeviceTypes.GENERIC_DEVICES:
          return this.$t("settings.exportSettings.otherDevices").toString();
        case DeviceTypes.ALL_DEVICES:
          return this.$t("settings.exportSettings.allDevices").toString();
        case DeviceTypes.IRES_DEVICES:
          return this.$t("settings.exportSettings.hyDryDevices").toString();
      }
    };
  }

  get formatDeviceFilterOptions() {
    return (filter: GenericDeviceFilter) => {
      return getGenericFilterName(this, filter);
    };
  }

  async created() {
    this.isLoading = true;

    this.clientPanels = await getAvailablePanels();
    this.devices = await getClientDevices();

    this.isLoading = false;
  }

  goBack() {
    this.$router.back();
  }

  matchesDeviceFilter(
    device: GenericDevice | ClientPanel,
    isClientPanel: boolean,
  ) {
    switch (this.deviceFilter) {
      case GenericDeviceFilter.ALL_DEVICES:
        return true;
      case GenericDeviceFilter.IN_USAGE:
        return isClientPanel
          ? (device as ClientPanel).currentProjectId !== null
          : (device as GenericDevice).currentAssignedProjectId;
      case GenericDeviceFilter.AVAILABLE:
        return isClientPanel
          ? !(device as ClientPanel).currentProjectId && isDeviceAvailable(device)
          : !(device as GenericDevice).currentAssignedProjectId && isDeviceAvailable(device);
      case GenericDeviceFilter.NOT_AVAILABLE:
        return !isDeviceAvailable(device);
    }
  }

  async exportToCSV() {
    this.isLoadingExport = true;
    // Yield to browser render loop so the loading spinner appears
    await new Promise((resolve) => setTimeout(resolve, 50));

    const headers = getLocalizedCSVHeaders((key) => this.$t(key).toString());
    const csvRows: string[] = [];
    const headerRow = headers.join(CSV_SEPARATOR);
    
    csvRows.push(headerRow);
    
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.devices.sort((a, b) => a.inventoryNumber!.localeCompare(b.inventoryNumber!))
    
    this.allDevices = [
      ...(this.devicesSelection === DeviceTypes.ALL_DEVICES || this.devicesSelection === DeviceTypes.GENERIC_DEVICES)
        ? filterRetainedDevices(this.devices)
          .filter((device: GenericDevice) =>
            this.matchesDeviceFilter(
              device,
              false
            ) 
          )
        : [], 
      ...(this.devicesSelection === DeviceTypes.ALL_DEVICES || this.devicesSelection === DeviceTypes.IRES_DEVICES)
        ? filterRetainedDevices(this.clientPanels)
          .filter((device: ClientPanel) =>
            this.matchesDeviceFilter(
              device,
              true
            )
          )
        : []
      ];

    for (const item of this.allDevices) {
      const isInPanels = this.clientPanels.find((panel) => {
        return panel.serialNumber == item.serialNumber
      })

      let deviceRow = ""
      
      if (isInPanels) {
        const device = item as ClientPanel
        const genDevice: GenericDevice = clientPanelToGenericDevice(device)
        const categoryName = getCategoryName(this.deviceSettingsJson, genDevice.categoryId)
        const deviceName = getDeviceName(this.deviceSettingsJson, genDevice.templateId, genDevice.categoryId)
        
        deviceRow = createLine(
          this.deviceSettingsJson, 
          categoryName, 
          deviceName, 
          genDevice, 
          null, 
          true
        )
        .join(CSV_SEPARATOR)
      } else {
        const device = item as GenericDevice

        const { updatedCategoryId, updatedTemplateId } = getUpdatedCategoryAndTemplateIds(
          device.categoryId,
          device.templateId,
          this.localDeviceSettingsJson,
          this.sharedDeviceSettingsJson,
          device.devicePowerUsageType ?? undefined,
        );

        device.categoryId = updatedCategoryId;
        device.templateId = updatedTemplateId;
        
        deviceRow = createLine(
          this.deviceSettingsJson, 
          getCategoryName(this.deviceSettingsJson, device.categoryId), 
          getDeviceName(this.deviceSettingsJson, device.templateId, device.categoryId), 
          device, 
          device.lastMeterReading
        )
        .join(CSV_SEPARATOR)
      }
      
      csvRows.push(deviceRow);
    }

    const csvContent = csvRows.join('\n');
    this.exportCsvContent = csvContent;
    this.exportHeaders = headers;
    this.exportDataRows = csvRows.slice(1);

    // Build DataViewer preview from the data rows (skip header)
    const dataRows = csvRows.slice(1);
    this.previewSections = [
      mapDeviceCsvToSection(
        headers,
        dataRows,
        "devices",
        this.$tc("settings.exportSettings.exportDialog.message", this.allDevices.length),
        false,
      ),
    ];
    this.showPreview = true;
    this.isLoadingExport = false;
  }

  /** Download the export in the selected format. */
  async downloadExport() {
    const fileName = `DEVICES-${getDateForFile(new Date())}`;
    await downloadDeviceExport(this.exportHeaders, this.exportDataRows, fileName, this.selectedFormat);
  }

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.hyDryDevice.devicesExport").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
